import React, { useState } from 'react';
import { Users, Plus, Mail, Edit, Shield, Clock } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { getInitials, formatDate, timeAgo } from '../utils/helpers';

const TeamManagement = () => {
    const { team, brands, activityLog, addTeamMember } = useApp();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteData, setInviteData] = useState({
        name: '',
        email: '',
        role: 'Creator',
        brands: [],
    });

    const roleOptions = ['Admin', 'Creator', 'Reviewer', 'Viewer'];

    const handleInvite = (e) => {
        e.preventDefault();
        addTeamMember(inviteData);
        setShowInviteModal(false);
        setInviteData({ name: '', email: '', role: 'Creator', brands: [] });
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            Admin: 'error',
            Creator: 'primary',
            Reviewer: 'warning',
            Viewer: 'neutral',
        };
        return colors[role] || 'neutral';
    };

    return (
        <div className="animate-fadeIn">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-8)' }}>
                <div>
                    <h1>Team Management</h1>
                    <p className="text-secondary">Manage team members, roles, and permissions.</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
                    <Plus size={20} />
                    Invite Member
                </button>
            </div>

            <div className="grid grid-cols-3 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                {/* Team Members */}
                <div className="card" style={{ gridColumn: 'span 2' }}>
                    <h2 style={{ marginBottom: 'var(--space-6)' }}>Team Members</h2>

                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Member</th>
                                    <th>Role</th>
                                    <th>Brands</th>
                                    <th>Last Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {team.map((member) => (
                                    <tr key={member.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                <div className="avatar avatar-sm">
                                                    {member.avatar ? <img src={member.avatar} alt={member.name} /> : getInitials(member.name)}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{member.name}</div>
                                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                        {member.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge badge-${getRoleBadgeColor(member.role)}`}>
                                                {member.role}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                                                {member.brands.map(brandId => {
                                                    const brand = brands.find(b => b.id === brandId);
                                                    return brand ? (
                                                        <span key={brandId} className="badge badge-neutral" style={{ fontSize: '10px' }}>
                                                            {brand.name}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}>
                                            {timeAgo(member.lastActive)}
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-ghost">
                                                <Edit size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Role Permissions */}
                <div className="card">
                    <h3 style={{ marginBottom: 'var(--space-4)' }}>Role Permissions</h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <Shield size={16} style={{ color: 'var(--color-error)' }} />
                                <div style={{ fontWeight: 600, color: 'var(--color-error)' }}>Admin</div>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)' }}>
                                <li>Full access</li>
                                <li>Manage team members</li>
                                <li>Configure automation</li>
                                <li>View all analytics</li>
                            </ul>
                        </div>

                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <Shield size={16} style={{ color: 'var(--color-primary)' }} />
                                <div style={{ fontWeight: 600, color: 'var(--color-primary)' }}>Creator</div>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)' }}>
                                <li>Create & edit creatives</li>
                                <li>Schedule posts</li>
                                <li>View analytics</li>
                            </ul>
                        </div>

                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <Shield size={16} style={{ color: 'var(--color-warning)' }} />
                                <div style={{ fontWeight: 600, color: 'var(--color-warning)' }}>Reviewer</div>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)' }}>
                                <li>Review & approve</li>
                                <li>Comment on creatives</li>
                                <li>View analytics</li>
                            </ul>
                        </div>

                        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <Shield size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                                <div style={{ fontWeight: 600, color: 'var(--color-text-tertiary)' }}>Viewer</div>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)' }}>
                                <li>View creatives</li>
                                <li>View analytics</li>
                                <li>Read-only access</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Log */}
            <div className="card">
                <h2 style={{ marginBottom: 'var(--space-6)' }}>Recent Activity</h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {activityLog.slice(0, 10).map((activity) => (
                        <div
                            key={activity.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                            }}
                        >
                            <Clock size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                            <div style={{ flex: 1 }}>
                                <span style={{ fontWeight: 600 }}>{activity.user}</span>
                                {' '}{activity.action}{' '}
                                <span style={{ color: 'var(--color-primary)' }}>{activity.target}</span>
                            </div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                {timeAgo(activity.timestamp)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="modal-backdrop" onClick={() => setShowInviteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Invite Team Member</h2>
                            <button className="btn btn-icon btn-ghost" onClick={() => setShowInviteModal(false)}>×</button>
                        </div>

                        <form onSubmit={handleInvite}>
                            <div className="modal-body">
                                <div className="input-group">
                                    <label className="input-label">Name*</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        value={inviteData.name}
                                        onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Email*</label>
                                    <input
                                        type="email"
                                        className="input-field"
                                        value={inviteData.email}
                                        onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Role*</label>
                                    <select
                                        className="select-field"
                                        value={inviteData.role}
                                        onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                                    >
                                        {roleOptions.map(role => (
                                            <option key={role} value={role}>{role}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="input-group">
                                    <label className="input-label">Assign Brands</label>
                                    {brands.map(brand => (
                                        <label key={brand.id} className="checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                className="checkbox"
                                                checked={inviteData.brands.includes(brand.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setInviteData({ ...inviteData, brands: [...inviteData.brands, brand.id] });
                                                    } else {
                                                        setInviteData({ ...inviteData, brands: inviteData.brands.filter(id => id !== brand.id) });
                                                    }
                                                }}
                                            />
                                            <span>{brand.logo} {brand.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <Mail size={16} />
                                    Send Invitation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamManagement;
